/**
 * Shared pending-queue counts for admin shell + dashboard home.
 * Avoids double-fetch of memberships/free-members on every /admin paint.
 */

export type AdminPendingQueues = {
  memberships: number;
  free: number;
};

type CacheEntry = AdminPendingQueues & { at: number };

const TTL_MS = 45_000;
let cache: CacheEntry | null = null;
let inflight: Promise<AdminPendingQueues> | null = null;

type AdminFetch = (input: string, init?: RequestInit) => Promise<Response>;

function isFresh(entry: CacheEntry | null): entry is CacheEntry {
  return Boolean(entry && Date.now() - entry.at < TTL_MS);
}

/** Synchronous read (may be stale/null). Used for first paint. */
export function peekAdminPendingQueues(): AdminPendingQueues | null {
  if (!cache) return null;
  return { memberships: cache.memberships, free: cache.free };
}

/**
 * Fetch pending membership + free-member counts (deduped + short TTL).
 * force=true bypasses TTL (manual refresh).
 */
export async function loadAdminPendingQueues(
  adminFetch: AdminFetch,
  opts?: { force?: boolean },
): Promise<AdminPendingQueues> {
  if (!opts?.force && isFresh(cache)) {
    return { memberships: cache.memberships, free: cache.free };
  }
  if (inflight && !opts?.force) return inflight;

  inflight = (async () => {
    let memberships = 0;
    let free = 0;
    try {
      const [memRes, freeRes] = await Promise.all([
        adminFetch("/api/admin/memberships?status=pending_approval"),
        adminFetch("/api/admin/free-members"),
      ]);
      if (memRes.ok) {
        const json = await memRes.json().catch(() => null);
        const rows = Array.isArray(json?.memberships)
          ? json.memberships
          : Array.isArray(json)
            ? json
            : [];
        memberships = rows.length;
      }
      if (freeRes.ok) {
        const json = await freeRes.json().catch(() => null);
        const rows = Array.isArray(json?.members) ? json.members : [];
        free = rows.length;
      }
    } catch {
      /* keep zeros / previous */
      if (cache) {
        return { memberships: cache.memberships, free: cache.free };
      }
    }
    cache = { memberships, free, at: Date.now() };
    return { memberships, free };
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
